import { assign, createMachine } from "xstate";
import { IOfferMode } from "@nju/data-access-portal-offers";
import {
  createOrchestrator,
  fetchOrchestrators,
  IFtthOrchestrator,
  IMobileOrchestrator,
  IMultiOrderOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { cancelOrder, createFtthOrder } from "@nju/data-access-portal-order";
import { fetchOrderProducts } from "@nju/data-access-portal-product";
import { fetchActionToken, register } from "@nju/data-access-sales-user";
import { BasicError } from "@nju/result";
import { generalErrorMessage, isValidEmail } from "@nju/utils";

interface IContext {
  email: string;
  offerIds?: string[];
  formError?: string;
  mode: IOfferMode;
  numberOfRetries: number;
}

type IEvent =
  | { type: "SUBMIT" }
  | { type: "GENERAL_ERROR" }
  | { type: "CREATE_ORDER" }
  | { type: "CLIENT_LOCKED_BY_OTHER_AGENT" }
  | { type: "GO_TO_CLIENT_STATUS" }
  | { type: "GO_TO_MULTI_CLIENT_STATUS" }
  | { type: "CHECK_CLIENT_INSTALLED" }
  | { type: "ANOTHER_ORDER_IN_PROGRESS" }
  | { type: "CREATE_ORCHESTRATOR_ERROR" }
  | {
      type: "REGISTRATION_SUCCESSFUL";
      orchestrator?:
        | IFtthOrchestrator
        | IMobileOrchestrator
        | IMultiOrderOrchestrator;
    }
  | {
      type: "PENDING_CANCELLATION";
    }
  | {
      type: "SET_INPUT_VALUE";
      name: "email";
      value: string;
    };

export const registrationMachine = createMachine<IContext, IEvent>(
  {
    context: {
      email: "",
      mode: "ftth",
      numberOfRetries: 0,
    },
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "ready",
    states: {
      ready: {
        on: {
          SET_INPUT_VALUE: {
            actions: "setInputValue",
          },
          SUBMIT: {
            target: "validate",
          },
        },
      },
      validate: {
        entry: "validateForm",
        always: [
          {
            cond: "hasNoInputErrors",
            target: "fetchClientToken",
          },
          {
            target: "ready",
          },
        ],
      },
      resolveMode: {
        always: [
          {
            cond: "isFtth",
            target: "createOrder",
          },
          {
            target: "createMobileOrder",
          },
        ],
      },
      createMobileOrder: {
        invoke: {
          src: "createMultiOrder",
        },
        on: {
          CLIENT_LOCKED_BY_OTHER_AGENT: {
            actions: "redirectToClientInProgress",
          },
          GO_TO_CLIENT_STATUS: {
            actions: "redirectToClientStatus",
          },
          GO_TO_MULTI_CLIENT_STATUS: {
            actions: "redirectToCustomerStatusPanel",
          },
          REGISTRATION_SUCCESSFUL: {
            actions: "redirectToOrchestratorState",
          },
          GENERAL_ERROR: {
            actions: "setFormError",
            target: "ready",
          },
          CREATE_ORCHESTRATOR_ERROR: {
            actions: "setNumberOfRetries",
            target: "idleMobileOrder",
          },
        },
      },
      idleMobileOrder: {
        after: [
          {
            delay: 2000,
            target: "createMobileOrder",
          },
        ],
      },
      fetchClientToken: {
        invoke: {
          src: "fetchClientToken",
        },
        on: {
          CHECK_CLIENT_INSTALLED: {
            target: "resolveMode",
          },
          GO_TO_CLIENT_STATUS: {
            actions: "redirectToClientStatus",
          },
          GENERAL_ERROR: {
            actions: "setFormError",
            target: "ready",
          },
        },
      },
      checkClientInstalled: {
        invoke: {
          src: "checkClientInstalled",
        },
        on: {
          CREATE_ORDER: {
            target: "createOrder",
          },
          GO_TO_CLIENT_STATUS: {
            actions: "redirectToClientStatus",
          },
          GENERAL_ERROR: {
            actions: "setFormError",
            target: "ready",
          },
        },
      },
      createOrder: {
        invoke: {
          src: "createFtthOrder",
        },
        on: {
          CLIENT_LOCKED_BY_OTHER_AGENT: {
            actions: "redirectToClientInProgress",
          },
          GO_TO_CLIENT_STATUS: {
            actions: "redirectToClientStatus",
          },
          REGISTRATION_SUCCESSFUL: {
            actions: "redirectToOrchestratorState",
          },
          PENDING_CANCELLATION: {
            target: "pendingCancellation",
          },
          ANOTHER_ORDER_IN_PROGRESS: {
            actions: "redirectToAnotherProcessInProgress",
          },
          GENERAL_ERROR: {
            actions: "setFormError",
            target: "ready",
          },
          CREATE_ORCHESTRATOR_ERROR: {
            actions: "setNumberOfRetries",
            target: "idleFtthOrder",
          },
        },
      },
      idleFtthOrder: {
        after: [
          {
            delay: 2000,
            target: "createOrder",
          },
        ],
      },
      pendingCancellation: {
        on: {
          CREATE_ORDER: {
            target: "createOrder",
          },
        },
      },
    },
  },
  {
    services: {
      // this logic check whether we have already client in keycloak
      // if checkClientInKeycloak return error, we need to create it with register endpoint
      // if checkClientInKeycloak return success, we just set access token to local storage and go further
      fetchClientToken: ({ email, mode }) => {
        return async (callback) => {
          const checkClientInKeycloak = await fetchActionToken({ email });

          if (checkClientInKeycloak.isError) {
            if (checkClientInKeycloak.err.code === "CLIENT_NOT_FOUND") {
              const registerToken = await register({ email });

              if (registerToken.isError) {
                return callback({ type: "GENERAL_ERROR" });
              }

              return callback({ type: "CHECK_CLIENT_INSTALLED" });
            }

            return callback({ type: "GENERAL_ERROR" });
          }

          if (mode === "mobile") {
            return callback({ type: "GO_TO_CLIENT_STATUS" });
          }

          return callback({ type: "CHECK_CLIENT_INSTALLED" });
        };
      },
      checkClientInstalled: () => {
        return async (callback) => {
          try {
            const products = (await fetchOrderProducts()).unwrap();

            if (products.ftthProducts.access) {
              return callback({ type: "GO_TO_CLIENT_STATUS" });
            }

            return callback({ type: "CREATE_ORDER" });
          } catch {
            callback({ type: "GENERAL_ERROR" });
          }
        };
      },
      createMultiOrder: ({ numberOfRetries }) => {
        return async (callback) => {
          const result = await fetchOrchestrators();

          if (result.isError) {
            if (result.err.code === "CLIENT_LOCKED_BY_OTHER_AGENT") {
              return callback({ type: "CLIENT_LOCKED_BY_OTHER_AGENT" });
            }

            return callback({ type: "GENERAL_ERROR" });
          }

          const onboarding = result.value.multiOrder;

          try {
            if (!onboarding) {
              const result = await createOrchestrator(
                "NJU_MULTIORDER_ONBOARDING",
                {},
              );

              if (result.isError) {
                if (
                  result.err.code === "422_UNPROCESSABLE_CONTENT" &&
                  numberOfRetries < 8
                ) {
                  return callback({ type: "CREATE_ORCHESTRATOR_ERROR" });
                }
                return callback({ type: "GENERAL_ERROR" });
              }

              if (!result.value) {
                throw new BasicError({
                  message: "Missing orchestrator",
                });
              }

              return callback({
                type: "REGISTRATION_SUCCESSFUL",
                orchestrator: result.unwrap(),
              });
            }

            return callback({
              type: "GO_TO_MULTI_CLIENT_STATUS",
            });
          } catch {
            return callback({ type: "GENERAL_ERROR" });
          }
        };
      },
      createFtthOrder: ({ offerIds, numberOfRetries }) => {
        return async (callback) => {
          const installationToken =
            window.sessionStorage.getItem("installationToken");

          if (!installationToken || !offerIds) {
            return callback({ type: "GENERAL_ERROR" });
          }

          const result = await fetchOrchestrators();

          if (result.isError) {
            if (result.err.code === "CLIENT_LOCKED_BY_OTHER_AGENT") {
              return callback({ type: "CLIENT_LOCKED_BY_OTHER_AGENT" });
            }

            return callback({ type: "GENERAL_ERROR" });
          }

          const onboarding = result.value.ftth;

          try {
            if (!onboarding) {
              const result = await createOrchestrator(
                "NJU_FTTH_ONBOARDING",
                {},
              );

              if (result.isError) {
                if (result.err.code === "CLIENT_HAS_UNPAID_INVOICES") {
                  return callback({ type: "GO_TO_CLIENT_STATUS" });
                }

                if (
                  result.err.code === "422_UNPROCESSABLE_CONTENT" &&
                  numberOfRetries < 8
                ) {
                  return callback({ type: "CREATE_ORCHESTRATOR_ERROR" });
                }

                return callback({ type: "GENERAL_ERROR" });
              }

              if (!result.value) {
                throw new BasicError({
                  message: "Missing ftth orchestrator",
                });
              }

              const { id: orchestratorId } = result.value;

              if (installationToken) {
                if (!Array.isArray(offerIds)) {
                  return callback({ type: "GENERAL_ERROR" });
                }

                const orchestratorWithOrder = await createFtthOrder({
                  orchestratorId,
                  mode: "ftth",
                  installationToken,
                  offerIds,
                });

                if (orchestratorWithOrder.isError) {
                  if (
                    orchestratorWithOrder.err.code ===
                    "ANOTHER_ORDER_IN_PROGRESS"
                  ) {
                    return callback({ type: "ANOTHER_ORDER_IN_PROGRESS" });
                  }
                  return callback({ type: "GENERAL_ERROR" });
                }

                return callback({
                  type: "REGISTRATION_SUCCESSFUL",
                  orchestrator: orchestratorWithOrder.value,
                });
              }
            }

            if (
              onboarding?.state !== "START" &&
              onboarding?.state !== "IDENTITY" &&
              onboarding?.state !== "PAYMENT_METHOD" &&
              onboarding?.state !== "IDENTITY_PENDING" &&
              onboarding?.state !== "APPOINTMENT" &&
              onboarding?.state !== "ORDER_SUMMARY"
            ) {
              return callback({
                type: "GO_TO_CLIENT_STATUS",
              });
            }

            if (onboarding.state === "START") {
              const orchestratorWithOrder = await createFtthOrder({
                mode: "ftth",
                orchestratorId: onboarding.id,
                installationToken,
                offerIds,
              });

              if (
                orchestratorWithOrder.isError &&
                orchestratorWithOrder.err.code === "ANOTHER_ORDER_IN_PROGRESS"
              ) {
                return callback({ type: "ANOTHER_ORDER_IN_PROGRESS" });
              }

              return callback({
                type: "REGISTRATION_SUCCESSFUL",
                orchestrator: orchestratorWithOrder.unwrap(),
              });
            }

            if (
              onboarding.state === "IDENTITY" ||
              onboarding.state === "PAYMENT_METHOD" ||
              onboarding.state === "IDENTITY_PENDING" ||
              onboarding.state === "APPOINTMENT" ||
              onboarding.state === "ORDER_SUMMARY"
            ) {
              await cancelOrder({
                reason: "agentRequest",
                orchestratorId: onboarding.id,
                mode: "ftth",
              });

              const orchestratorWithOrder = await createFtthOrder({
                mode: "ftth",
                orchestratorId: onboarding.id,
                installationToken,
                offerIds,
              });

              if (
                orchestratorWithOrder.isError &&
                orchestratorWithOrder.err.code ===
                  "ORDER_CANCEL_IN_PENDING_CANCELLATION"
              ) {
                return callback({
                  type: "PENDING_CANCELLATION",
                });
              }

              return callback({
                type: "REGISTRATION_SUCCESSFUL",
                orchestrator: orchestratorWithOrder.unwrap(),
              });
            }

            return callback({
              type: "REGISTRATION_SUCCESSFUL",
              orchestrator: onboarding,
            });
          } catch {
            return callback({ type: "GENERAL_ERROR" });
          }
        };
      },
    },
    actions: {
      setFormError: assign<IContext, IEvent>((context, event) => {
        if (event.type === "GENERAL_ERROR") {
          return {
            formError: generalErrorMessage,
          };
        }

        return context;
      }),
      setNumberOfRetries: assign<IContext, IEvent>((context, event) => {
        if (event.type === "CREATE_ORCHESTRATOR_ERROR") {
          return {
            numberOfRetries: context.numberOfRetries + 1,
          };
        }

        return context;
      }),
      validateForm: assign<IContext, IEvent>((context) => {
        let formError: string | undefined;

        if (!context.email.trim()) {
          formError = "Wpisz adres e-mail.";
        } else if (!isValidEmail(context.email)) {
          formError = "Wpisz poprawny adres e-mail.";
        }

        return { formError };
      }),
      setInputValue: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_INPUT_VALUE") {
          return {
            [event.name]: event.value,
          };
        }

        return context;
      }),
    },
    guards: {
      hasNoInputErrors: ({ formError }) => {
        return formError === undefined;
      },
      isFtth: ({ mode }) => {
        return mode === "ftth";
      },
    },
  },
);
